<template>
  <div
    class="con-select"
    :class="[
      $attrs.class,
      {
        autocompletex: autocomplete,
        activeOptions: active,
        'input-select-validate-success': success,
        'input-select-validate-danger': danger,
        'input-select-validate-warning': warning,
      },
    ]"
    :style="[$attrs.style, selectStyle]"
  >
    <label v-if="label" ref="inputSelectLabel" class="vs-select--label">
      {{ label }}
    </label>
    <div class="input-select-con">
      <input
        ref="inputselect"
        class="input-select vs-select--input"
        :readonly="!autocomplete"
        type="text"
        :value="displayValue"
        v-bind="inputAttrs"
        @blur="handleBlur"
        @focus="handleFocus"
        @mouseup="openOptions"
        @input="handleInput"
        @keyup="handleKeyup"
        @keydown.esc.stop.prevent="closeOptions"
      />
      <button
        class="icon-select-clear vs-select--icon-clear"
        :class="{ activeBtnClear }"
        type="button"
        @click="clearValue"
      >
        <i class="material-icons">{{ iconClear }}</i>
      </button>
      <vs-icon
        v-if="!activeBtnClear"
        class="icon-select vs-select--icon"
        :icon-pack="iconPack"
        :icon="icon"
      />

      <Teleport to="body">
        <transition name="fadeselect">
          <div
            v-show="active"
            ref="vsSelectOptions"
            class="vs-select--options"
            :class="[`vs-select-${color}`, { scrollx }]"
            :style="coordinates"
          >
            <ul ref="ulx"><slot /></ul>
            <ul v-show="noVisibleItems">
              <li @click="clearFilter">{{ noData }}</li>
            </ul>
          </div>
        </transition>
      </Teleport>
    </div>

    <transition-group
      @before-enter="beforeEnter"
      @enter="enter"
      @leave="leave"
    >
      <div v-if="success" key="success" class="con-text-validation">
        <span class="span-text-validation span-text-validation-success">
          {{ successText }}
        </span>
      </div>
      <div
        v-else-if="danger"
        key="danger"
        class="con-text-validation span-text-validation-danger"
      >
        <span class="span-text-validation">{{ dangerText }}</span>
      </div>
      <div
        v-else-if="warning"
        key="warning"
        class="con-text-validation span-text-validation-warning"
      >
        <span class="span-text-validation">{{ warningText }}</span>
      </div>
      <div
        v-if="descriptionText"
        key="description"
        class="con-text-validation span-text-validation"
      >
        <span class="span-text-validation">{{ descriptionText }}</span>
      </div>
    </transition-group>
  </div>
</template>

<script>
export default {
  name: "VsSelect",
  inheritAttrs: false,
  props: {
    modelValue: {
      default: undefined,
    },
    value: {
      default: undefined,
    },
    noData: {
      type: String,
      default: "No data available",
    },
    maxSelected: {
      type: [Number, String],
      default: null,
    },
    autocomplete: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: "primary",
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: null,
    },
    success: {
      type: Boolean,
      default: false,
    },
    danger: {
      type: Boolean,
      default: false,
    },
    warning: {
      type: Boolean,
      default: false,
    },
    successText: {
      type: String,
      default: null,
    },
    dangerText: {
      type: String,
      default: null,
    },
    warningText: {
      type: String,
      default: null,
    },
    descriptionText: {
      type: String,
      default: null,
    },
    iconPack: {
      type: String,
      default: "material-icons",
    },
    icon: {
      type: String,
      default: "keyboard_arrow_down",
    },
    iconClear: {
      type: String,
      default: "close",
    },
    width: {
      type: String,
      default: null,
    },
  },
  emits: [
    "update:modelValue",
    "input",
    "change",
    "input-change",
    "blur",
    "focus",
  ],
  provide() {
    return {
      vsSelect: this,
    };
  },
  data() {
    return {
      active: false,
      filterValue: "",
      coordinates: {},
      items: [],
      scrollx: false,
    };
  },
  computed: {
    currentValue() {
      return this.modelValue !== undefined ? this.modelValue : this.value;
    },
    activeBtnClear() {
      return this.autocomplete && Boolean(this.filterValue);
    },
    selectStyle() {
      return this.width ? { width: this.width } : null;
    },
    visibleItems() {
      return this.items.filter((item) => item.visible);
    },
    noVisibleItems() {
      return this.visibleItems.length === 0;
    },
    displayValue() {
      if (this.autocomplete && this.filterValue) return this.filterValue;
      if (this.multiple) {
        const values = Array.isArray(this.currentValue)
          ? this.currentValue
          : [];
        return this.items
          .filter((item) => values.some((value) => value == item.value))
          .map((item) => item.text)
          .join(",");
      }
      const selected = this.items.find(
        (item) => item.value == this.currentValue
      );
      return selected ? selected.text : this.currentValue || "";
    },
    inputAttrs() {
      const attrs = { ...this.$attrs };
      [
        "class",
        "style",
        "onBlur",
        "onFocus",
        "onMouseup",
        "onInput",
        "onKeyup",
        "onKeydown",
      ].forEach((key) => delete attrs[key]);
      return attrs;
    },
  },
  watch: {
    currentValue(value, oldValue) {
      if (value !== oldValue) this.$emit("change", value);
    },
    active(value) {
      if (value) {
        this.$nextTick(() => {
          this.updatePosition();
          this.scrollx = this.$refs.ulx.scrollHeight >= 260;
          const selected = this.items.find((item) => item.isActive);
          const first = selected || this.visibleItems[0];
          if (!this.autocomplete && first) first.focus();
        });
      }
    },
  },
  beforeUnmount() {
    document.removeEventListener("click", this.clickBlur);
  },
  methods: {
    registerItem(item) {
      this.items.push(item);
    },
    unregisterItem(item) {
      const index = this.items.indexOf(item);
      if (index !== -1) this.items.splice(index, 1);
    },
    emitValue(value) {
      this.$emit("update:modelValue", value);
      this.$emit("input", value);
    },
    selectItem(item) {
      if (item.disabled || item.disabledByLimit) return;
      if (this.multiple) {
        const values = Array.isArray(this.currentValue)
          ? this.currentValue.slice()
          : [];
        const index = values.indexOf(item.value);
        if (index === -1) values.push(item.value);
        else values.splice(index, 1);
        this.emitValue(values);
        if (this.autocomplete) {
          this.filterItems("");
          this.$nextTick(() => this.$refs.inputselect.focus());
        }
      } else {
        this.emitValue(item.value);
        this.closeOptions();
      }
    },
    openOptions() {
      if (this.$attrs.disabled) return;
      this.active = true;
      document.addEventListener("click", this.clickBlur);
      this.setLabelActive(true);
      this.$nextTick(this.updatePosition);
    },
    handleFocus(event) {
      this.$emit("focus", event);
      this.openOptions();
    },
    handleBlur(event) {
      if (
        this.autocomplete &&
        event.relatedTarget &&
        !event.relatedTarget.closest(".vs-select--options")
      ) {
        this.closeOptions();
      }
      this.$emit("blur", event);
    },
    handleInput(event) {
      if (!this.autocomplete) return;
      this.filterValue = event.target.value;
      this.filterItems(this.filterValue);
      this.$emit("input-change", event);
    },
    handleKeyup(event) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        const item =
          event.key === "ArrowDown"
            ? this.visibleItems[0]
            : this.visibleItems[this.visibleItems.length - 1];
        if (item) item.focus();
      }
    },
    filterItems(value) {
      this.filterValue = value;
      const query = value.split(",").pop().trim().toUpperCase();
      this.items.forEach((item) => {
        item.visible =
          !query || String(item.text).toUpperCase().includes(query);
        item.filterValue = query;
      });
      this.$nextTick(this.updatePosition);
    },
    clearValue() {
      this.openOptions();
      this.filterItems("");
      this.$nextTick(() => this.$refs.inputselect.focus());
    },
    clearFilter() {
      this.filterItems("");
    },
    clickBlur(event) {
      if (
        event.target === this.$refs.inputselect ||
        event.target.closest(".vs-select--options")
      ) {
        return;
      }
      this.closeOptions();
      if (this.autocomplete) this.filterItems("");
    },
    closeOptions() {
      this.active = false;
      this.setLabelActive(false);
      document.removeEventListener("click", this.clickBlur);
    },
    updatePosition() {
      const input = this.$refs.inputselect;
      const options = this.$refs.vsSelectOptions;
      if (!input || !options) return;
      const rect = input.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      let top = rect.top + input.clientHeight + scrollTop + 5;
      if (rect.top + options.scrollHeight + 20 >= window.innerHeight) {
        top =
          rect.top +
          input.clientHeight +
          scrollTop -
          options.scrollHeight -
          (this.autocomplete ? input.clientHeight + 5 : 0);
      }
      this.coordinates = {
        left: `${rect.left}px`,
        top: `${top}px`,
        width: `${input.offsetWidth}px`,
      };
    },
    setLabelActive(active) {
      const label = this.$refs.inputSelectLabel;
      if (!label) return;
      label.classList.toggle(
        `input-select-label-${this.color}--active`,
        active
      );
    },
    beforeEnter(element) {
      element.style.height = 0;
    },
    enter(element, done) {
      element.style.height = `${element.scrollHeight}px`;
      done();
    },
    leave(element) {
      element.style.height = "0px";
    },
  },
};
</script>
